import React, { useRef, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGetVersionDetailsQuery } from '@/services/RoutingManagementService';
import mapboxgl from 'mapbox-gl';
import { KeenIcon } from '@/components';

import { useMapboxAccessToken } from '@/hooks/useMapboxAccessToken';
import { formatDisplayDateTime } from '@/utils/Date';

export const VersionDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const versionId = Number(id);
  const navigate = useNavigate();
  const location = useLocation();
  type VersionDetailsType = {
    geojson: GeoJSON.FeatureCollection;
    created_at: string;
    is_current: boolean;
    version: number;
    merged_filename: string;
  };

  const versionDetails = location.state
    ?.versionDetails as VersionDetailsType | null;

  const {
    data: fetchedVersionDetailsRaw,
    isLoading,
    isError,
    error,
  } = useGetVersionDetailsQuery(versionId, { skip: !!versionDetails });

  // Ensure fetchedVersionDetails always has geojson property
  const fetchedVersionDetails: VersionDetailsType | null =
    fetchedVersionDetailsRaw && 'geojson' in fetchedVersionDetailsRaw
      ? (fetchedVersionDetailsRaw as VersionDetailsType)
      : null;

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const { isMapboxReady } = useMapboxAccessToken();
  const [webglError, setWebglError] = useState(false);

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    const currentVersionDetails = versionDetails || fetchedVersionDetails;
    if (
      !currentVersionDetails?.geojson ||
      !mapContainerRef.current ||
      !isMapboxReady
    )
      return;

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    let center: [number, number] = [24.9384, 60.1699];
    if (
      currentVersionDetails.geojson.features &&
      currentVersionDetails.geojson.features.length > 0
    ) {
      const geometry = currentVersionDetails.geojson.features[0].geometry;
      let coords: unknown = null;
      if (geometry.type !== 'GeometryCollection' && 'coordinates' in geometry) {
        coords = (
          geometry as
            | GeoJSON.Point
            | GeoJSON.LineString
            | GeoJSON.Polygon
            | GeoJSON.MultiPoint
            | GeoJSON.MultiLineString
            | GeoJSON.MultiPolygon
        ).coordinates;
        const getFirstPosition = (coord: unknown): [number, number] | null => {
          if (Array.isArray(coord) && coord.length > 0) {
            if (typeof coord[0] === 'number' && coord.length >= 2) {
              return [coord[0], coord[1]];
            } else if (Array.isArray(coord[0])) {
              return getFirstPosition(coord[0]);
            }
          }
          return null;
        };
        const firstPos = getFirstPosition(coords);
        if (firstPos) {
          center = firstPos;
        }
      }
    }

    if (!mapboxgl.supported()) {
      setWebglError(true);
      return;
    }

    try {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center,
        zoom: 6,
      });
    } catch (e) {
      console.error('WebGL Error:', e);
      setWebglError(true);
      return;
    }

    mapRef.current.on('load', () => {
      if (mapRef.current && currentVersionDetails.geojson) {
        if (mapRef.current.getSource('route')) {
          mapRef.current.removeLayer('route-line');
          mapRef.current.removeSource('route');
        }
        mapRef.current.addSource('route', {
          type: 'geojson',
          data: currentVersionDetails.geojson,
        });
        mapRef.current.addLayer({
          id: 'route-line',
          type: 'line',
          source: 'route',
          paint: {
            'line-color': '#007cbf',
            'line-width': 4,
            'line-opacity': 0.8,
          },
        });
      }
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [versionDetails, fetchedVersionDetails, isMapboxReady]);

  const currentVersionDetails = versionDetails || fetchedVersionDetails;

  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8">
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3 text-gray-700">
            <span className="text-sm font-medium">
              Loading version details...
            </span>
          </div>
        </div>
      ) : isError ? (
        <div className=" border border-red-200 rounded-xl p-6 shadow-md text-center">
          <p className="text-red-600 font-semibold text-lg">
            Error loading version details: {error?.toString()}
          </p>
          <button
            onClick={handleBack}
            className="mt-4 inline-flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-300"
          >
            <KeenIcon icon="arrow-left" className="mr-2" /> Back
          </button>
        </div>
      ) : !currentVersionDetails ? (
        <div className="bg-gray-100 border border-gray-200 rounded-xl p-6 shadow-md text-center">
          <p className="text-gray-700 font-medium text-lg">
            No version data found.
          </p>
          <button
            onClick={handleBack}
            className="mt-4 inline-flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-300"
          >
            <KeenIcon icon="arrow-left" className="mr-2 contrast:text-white" />{' '}
            Back
          </button>
        </div>
      ) : (
        <div className="card bg-gray-100 rounded-lg overflow-hidden">
          <div className="card-header flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Go back to route listings"
                title="Back to Routes"
              >
                <KeenIcon icon="arrow-left" className="text-gray-600 text-lg" />
              </button>
              <h1 className="text-xl font-semibold text-gray-800">
                Version {currentVersionDetails.version} Details
              </h1>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <KeenIcon icon="map" className="text-lg" />
              <span className="text-sm">GeoJSON Route Visualization</span>
            </div>
          </div>
          <div className="card-body p-6">
            {webglError ? (
              <div className="w-full min-h-[500px] flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200 p-4 sm:p-8 shadow-sm">
                <div className="max-w-md text-center">
                  <div className="flex justify-center mb-4">
                    <KeenIcon
                      icon="shield-cross"
                      className="text-4xl text-danger"
                    />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Map Display Not Available
                  </h3>
                  <p className="text-gray-600 mb-6 font-medium">
                    The map cannot be displayed because WebGL (hardware
                    acceleration) is not available in your browser.
                  </p>
                  <div className="bg-gray-200 p-5 rounded-xl border border-gray-200 text-left shadow-sm">
                    <p className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-3">
                      How to enable
                    </p>
                    <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                      {`To enable hardware acceleration:

• Chrome/Edge: Go to Settings → System → Enable "Use hardware acceleration when available"
• Firefox: Go to Settings → General → Performance → Uncheck "Use recommended performance settings" → Check "Use hardware acceleration when available"

After making changes, please restart your browser.`}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div
                ref={mapContainerRef}
                className="w-full h-[500px] sm:h-[600px] lg:h-[700px]"
                aria-label="Map displaying route"
              />
            )}
            <div className="p-6"></div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-200 p-4 rounded-lg shadow-inner">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  Version Info
                </h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Filename</span>
                    <span className="text-sm font-medium text-gray-900">
                      {currentVersionDetails.merged_filename}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Created At</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatDisplayDateTime(new Date(
                        currentVersionDetails.created_at
                      ))}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Status</span>
                    <span
                      className={`px-2 py-1 rounded-full text-sm font-medium ${
                        currentVersionDetails.is_current
                          ? 'bg-green-100 dark:bg-green-800 contrast:bg-green-800 text-green-800 dark:text-green-200 contrast:text-green-100'
                          : 'bg-yellow-100 dark:bg-yellow-800 contrast:bg-yellow-800 text-yellow-800 dark:text-yellow-200 contrast:text-yellow-100'
                      }`}
                    >
                      {currentVersionDetails.is_current
                        ? 'Current'
                        : 'Not Current'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-200 p-4 rounded-lg shadow-inner">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  Route Stats
                </h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Features</span>
                    <span className="text-sm font-medium text-gray-900">
                      {currentVersionDetails.geojson.features.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                      Geometry Types
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      {[
                        ...new Set(
                          currentVersionDetails.geojson.features.map(
                            (f) => f.geometry.type
                          )
                        ),
                      ].join(', ')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionDetails;
